import type { ReactElement } from "react"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WidthOrg from "@onestore/hel/dist/components/organisms/WidthOrg"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import InfoBar from "@gatsby-plugin-generic-page/components/InfoBar"
import ProductBoxesGrid from "@gatsby-plugin-generic-page/components/ProductBoxesGrid"
import ProductBoxSchema from "@gatsby-plugin-generic-page/components/ProductBoxSchema"
import ProductBoxSlim from "@gatsby-plugin-generic-page/components/ProductBoxSlim"
import type { ProductBoxSectionFlatData } from "@gatsby-plugin-generic-page/fragments/productBoxSection"
import hasAnyOuterFooter from "@gatsby-plugin-generic-page/helpers/hasAnyOuterFooter"
import TogglePriceType from "~/components/TogglePriceType"
import isEmpty from "~/lib/isEmpty"
import ProductBoxPeriodsSwitcherSection from "./ProductBoxPeriodsSwitcherSection"

export type ProductBoxSectionProps = ProductBoxSectionFlatData & {
  // Escape hatch for consumers that need to override the container's padding
  // (e.g. on mobile) without changing containerSettings in Squidex — that
  // control only exposes padding as a side effect of the background choice.
  // Forwarded to BaseContainer's className, so use `!` modifiers (e.g.
  // "max-sm:p-4!") to reliably win over BaseContainer's own padding class.
  containerClassName?: string
}

// TODO [ONESTORE-540]: hasUpsellDrawer only has a visible effect inside
// cp-shop's ShopPage today (see ProductBoxWithSaleConfiguration, gated on
// useIsCpShop) — on other generic pages it now falls back to the normal
// config-drawer/basket/modal button instead of silently doing nothing
// (ONESTORE-738). Revisit once ONESTORE-540 makes the AX drawer itself
// available from any generic page.
export default function ProductBoxSection({
  header,
  boxes,
  sectionSettings,
  ctaElement,
  hasPeriodsSwitcher,
  periodsSwitcher,
  hasTogglePriceType,
  hasUpsellDrawer,
  infoBar,
  containerClassName,
}: ProductBoxSectionProps): ReactElement<ProductBoxSectionProps> {
  const productBoxesNumber = boxes.length
  const isSmallAndMediumScreen = useBreakpoint("small", "large")
  const boxesHaveAnyOuterFooter = hasAnyOuterFooter(boxes)

  if (hasPeriodsSwitcher && periodsSwitcher) {
    return (
      <ProductBoxPeriodsSwitcherSection
        header={header}
        boxes={boxes}
        sectionSettings={sectionSettings}
        ctaElement={ctaElement}
        periodsSwitcher={periodsSwitcher}
        hasTogglePriceType={hasTogglePriceType}
        hasUpsellDrawer={hasUpsellDrawer}
        infoBar={infoBar}
        hasAnyOuterFooter={boxesHaveAnyOuterFooter}
        containerClassName={containerClassName}
      />
    )
  }

  return (
    <>
      <ProductBoxSchema boxes={boxes} />

      <BaseSection
        {...sectionSettings}
        hasOverflowHidden={isSmallAndMediumScreen}
        role="region"
        aria-label="Selling Section"
      >
        <BaseContainer
          {...sectionSettings?.containerSettings}
          className={containerClassName}
        >
          {header ? (
            <PushOrg bottomSpace={hasTogglePriceType ? 4 : 8}>
              <Header {...header} />
            </PushOrg>
          ) : null}

          {hasTogglePriceType ? (
            <PushOrg bottomSpace={{ small: 2, medium: 3, large: 4.5 }}>
              <AlignContentOrg horizontalAlign="right">
                <TogglePriceType />
              </AlignContentOrg>
            </PushOrg>
          ) : null}

          <ProductBoxesGrid numberOfColumns={productBoxesNumber}>
            {boxes.map((box, index) => (
              <ProductBoxSlim
                key={box.id}
                index={index}
                productBox={box}
                hasAnyOuterFooter={boxesHaveAnyOuterFooter}
                hasUpsellDrawer={hasUpsellDrawer}
              />
            ))}
          </ProductBoxesGrid>

          {!isEmpty(ctaElement) ? (
            <PushOrg topSpace={6}>
              <AlignContentOrg horizontalAlign="center">
                <CtaElement ctaElement={ctaElement} />
              </AlignContentOrg>
            </PushOrg>
          ) : null}

          {!isEmpty(infoBar) ? (
            <PushOrg topSpace={5}>
              <AlignContentOrg horizontalAlign="center">
                <WidthOrg widthSpace={120}>
                  <InfoBar {...infoBar[0]} />
                </WidthOrg>
              </AlignContentOrg>
            </PushOrg>
          ) : null}
        </BaseContainer>
      </BaseSection>
    </>
  )
}
